// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { instanceToPlain, Type } from 'class-transformer'
import { CardString } from 'pokersolver'

import { getCardString, logger } from '../util'
import { Board, Game, Player, StreetAggressorEvent, Table } from './'
import { ActionEvaluator, ActionHint } from './ActionEvaluator'
import { Bet } from './Bet'
import { BetHint, BettingRecommender } from './BettingRecommender'
import { Blind } from './Blind'
import { Pot, PotKind, PotWinner } from './Pot'
import { BoardState, GameStates } from './states'

import type { GameState } from './states/GameState.types'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Snapshotter<T> {
  snapshot(): Snapshot
  snapshotForPlayer(playerId: string): Snapshot
}

class TableSnapshot {
  id: string
  name: string
  bigBlindPlayer: number
  bigBlindPlayerId: string
  buttonPlayer: number
  buttonPlayerId: string
  currentPlayer: number
  currentPlayerId: string
  smallBlindPlayer: number
  smallBlindPlayerId: string
  minPlayers: number
  maxPlayers: number
  tableState: string
  @Type(() => GameStates)
  state: GameState
  isSleeping: boolean
  isGameOverForced: boolean
  @Type(() => String)
  communityCards: CardString[]
  mainPot: number
  @Type(() => Pot)
  sidePots: Pot[]
  bigBlindAmount: number
  smallBlindAmount: number
  boardBets: {
    bets: Bet[]
    biggestBet: number
  }
  biggestBet: number
  boardState: BoardState['tag']
  @Type(() => PotWinner)
  winners: PotWinner[]
  @Type(() => BetHint)
  betHints: BetHint[]
  checkpoint: number
  minimumRaise: number
  nextBlinds: Blind
  updatedCurrentBlinds: Blind
  blindTimerCheckpoint: number
  @Type(() => Blind)
  blinds: Blind
  gameStartTime: number
  nextBlindIndex: number
  gamePauseTime: number
  playerActionTimeLeft: number
  isCardsVisibleByTeacher: boolean
  isCardsVisibleByEveryone: boolean
  isCardsVisibleOnShowDown: boolean
  arePlayersHaveFinishedBetting: boolean
  playerActionTime: number
  winnerModalCount: number
  isTableFull: boolean
  totalChips: number
  startTimerCheckpoint: number
  amountOfHands: number

  streetAggressorEvent?: StreetAggressorEvent[]
}

class Snapshot {
  gameId: string

  gameName: string

  @Type(() => GameStates)
  gameState: GameState

  @Type(() => TableSnapshot)
  table: Record<number, TableSnapshot>

  @Type(() => Player)
  players: Player[]

  @Type(() => Player)
  forPlayer?: Player

  @Type(() => Bet)
  withBet?: Bet

  currentPlayerAction: ActionHint[]

  @Type(() => Player)
  leftPlayers: Player[]

  @Type(() => [String])
  eventsToBroadcastAfterSleepIds?: string[]

  @Type(() => String)
  kind?: string
}

class GameSnapshotter implements Snapshotter<Game> {
  game: Game

  constructor(game: Game) {
    this.game = game
  }

  snapshot(): Snapshot {
    return new TableSnapshotter(this.game.defaultTable).snapshot()
  }

  snapshotForPlayer(playerId: string): Snapshot {
    return new TableSnapshotter(this.game.defaultTable).snapshotForPlayer(playerId)
  }

  static forGame(game: Game) {
    return new GameSnapshotter(game).snapshot()
  }

  static forGamePlayer(game: Game, playerId: string) {
    return new GameSnapshotter(game).snapshotForPlayer(playerId)
  }
}

class TableSnapshotter implements Snapshotter<Table> {
  constructor(public table: Table) {}

  // TODO: PlainClass Type?
  snapshot(): Snapshot {
    const table = this.table
    const players: Player[] = table.players.map((p) => instanceToPlain(p, { enableCircularCheck: true }))
    const leftPlayers: Player[] = table.leftPlayers.map((p) => instanceToPlain(p, { enableCircularCheck: true }))

    const tableSnap = { 0: this.snapshotForTable(table) }
    return <Snapshot>{
      gameId: table.gameId,
      gameName: table.gameName,
      gameState: table.state,
      table: tableSnap,
      players,
      // no me as we're snapshotting the entire table
      forPlayer: undefined,
      currentPlayerAction: [],
      leftPlayers
    }
  }

  snapshotForPlayer(playerId: string): Snapshot {
    const table: Table = this.table
    const players: Player[] = table.players.map((p) => instanceToPlain(p, { enableCircularCheck: true }))
    const leftPlayers: Player[] = table.leftPlayers.map((p) => instanceToPlain(p, { enableCircularCheck: true }))
    const forPlayer: Player = instanceToPlain(table.getPlayerById(playerId), { enableCircularCheck: true })

    let currentPlayerAction = forPlayer?.actions

    if (forPlayer?.actions.length === 0) {
      currentPlayerAction = ActionEvaluator.getActionHints(
        table.board,
        table.getPlayerById(forPlayer?.id),
        table.getBigBlindPlayer()
      )
    }
    return <Snapshot>{
      gameId: table.gameId,
      gameName: table.gameName,
      gameState: table.state,
      table: { 0: this.snapshotForTable(table) },
      players,
      forPlayer,
      currentPlayerAction,
      leftPlayers
    }
  }

  snapshotForPlayerWithBet(playerId: string, bet: Bet) {
    logger.trace(`[Snapshot.snapshotForPlayerWithBet] bet ${bet.kind} ${bet.amount} ${playerId}`)
    const snapshot = this.snapshotForPlayer(playerId)
    snapshot.withBet = bet
    return snapshot
  }

  snapshotForTable(table: Table) {
    return <TableSnapshot>{
      id: table.getId(),
      name: table.name,
      mode: table.mode,
      bigBlindPlayer: table.getBigBlindPlayer() ? table.getBigBlindPlayerIndex() : 0,
      bigBlindPlayerId: table.getBigBlindPlayer()?.getId(),
      buttonPlayer: table.getButtonPlayerIndex(),
      buttonPlayerId: table.getButtonPlayer()?.getId(),
      currentPlayer: table.getCurrentPlayerIndex(),
      currentPlayerId: table.getCurrentPlayer()?.getId(),
      smallBlindPlayer: table.getSmallBlindPlayer() ? table.getSmallBlindPlayerIndex() : 0,
      smallBlindPlayerId: table.getSmallBlindPlayer()?.getId(),
      minPlayers: table.minPlayers,
      maxPlayers: table.maxPlayers,
      tableState: table.internalState().tag,
      state: table.internalState(),
      isSleeping: table.isSleeping,
      isGameOverForced: table.isGameOverForced,
      communityCards: table.board.communityCards,
      mainPot: TableSnapshotter.getMainPotValue(table.board, table),
      sidePots: TableSnapshotter.getSidePots(table.board),
      bigBlindAmount: table.board.blinds.big,
      smallBlindAmount: table.board.blinds.small,
      boardBets: TableSnapshotter.getBoardBets(table),
      biggestBet: table.board.biggestBet(),
      boardState: table.board.internalState(),
      playersOnlyCalledBB: table.board.playersOnlyCalledBB,
      // @TODO: this is not working, causing client to disconnect and reconnect loop
      //winners,
      winners: TableSnapshotter.getWinnersFromPots(table.board),
      finalWinnerInfo: table.finalWinnerInfo,
      betHints: BettingRecommender.getBetHints(table.board),
      checkpoint: table.getCheckpoint(),
      startTimerCheckpoint: table.startTimerCheckpoint,
      minimumRaise: table.getMinimumRaise(),
      callAmount: table.callAmount,
      nextBlinds: table.nextBlinds,
      blindTimerCheckpoint: table.blindTimerCheckpoint,
      blinds: table.blinds,
      updatedCurrentBlinds: table.updatedCurrentBlinds,
      gameStartTime: table.gameStartTime,
      nextBlindIndex: table.nextBlindIndex,
      gamePauseTime: table.gamePauseTime,
      playerActionTimeLeft: table.getPlayerActionTimeLeft(),
      isCardsVisibleByTeacher: table.isCardsVisibleByTeacher,
      isCardsVisibleByEveryone: table.isCardsVisibleByEveryone,
      isCardsVisibleOnShowDown: table.isCardsVisibleOnShowDown,
      arePlayersHaveFinishedBetting: table.playersHaveFinishedBetting(),
      playerActionTime: table.playerActionTime || -1,
      winnerModalCount: TableSnapshotter.getWinnersFromPots(table.board).length,
      amountOfHands: table.amountOfHands,
      isTableFull: table.isTableFull,

      streetAggressorEvent: table?.streetAggressorEvent,

      // NOTE: we could remove this down the road, but with chips disappearing,
      // we currently snapshot the total chips on the table for each event to help with debugging
      totalChips:
        table.board.pot.amount +
        table.board.sidePots.reduce((sum, pot) => sum + pot.amount, 0) +
        table.players.reduce((sum, player) => sum + player.chips, 0) +
        table.players.reduce((sum, player) => sum + player.bets.reduce((betSum, bet) => betSum + bet.amount, 0), 0)
    }
  }

  static forTable(table: Table) {
    return new TableSnapshotter(table).snapshot()
  }

  static forPlayer(table: Table, playerId: string) {
    return new TableSnapshotter(table).snapshotForPlayer(playerId)
  }

  static forPlayerWithBet(table: Table, playerId: string, bet: Bet) {
    return new TableSnapshotter(table).snapshotForPlayerWithBet(playerId, bet)
  }

  static getWinnersFromPots(board: Board): PotWinner[] {
    let pots = board.getAllPots().reduce((winners, pot) => {
      if (pot.winners) {
        const mappedWinners = pot.winners.map((w) => ({ ...w, winningCards: w.winningCards.map(getCardString) }))
        winners = winners.concat(mappedWinners)
      }
      return winners
    }, [] as PotWinner[])
    if (pots.length > 0) {
      const mainPotWinner = pots.filter((w: PotWinner) => w.kind === PotKind.MAIN)
      const sidePotWinners = pots.filter((w: PotWinner) => w.kind === PotKind.SIDE)
      if (sidePotWinners.length > 0) {
        pots = mainPotWinner.concat(TableSnapshotter.reduceSidePots(sidePotWinners))
      }
    }
    return pots
  }

  static getSidePots(board: Board) {
    return board.getAllPots().filter((pot) => pot.kind === PotKind.SIDE)
  }

  static getBoardBets(table: Table) {
    return {
      bets: table.board.getBets(),
      biggestBet: table.board.biggestBet()
    }
  }

  static getMainPotValue(board: Board, table: Table) {
    if (board.pot.amount === 0 && table.getSitOutPlayers().length > 0) {
      return table.getSitOutPlayers().reduce((acc, player) => {
        acc += player.bet
        return acc
      }, 0)
    }
    return board.pot.amount
  }

  static reduceSidePots(winners: PotWinner[]) {
    const reducedWinnerData: PotWinner[] = []
    winners.forEach((ele: PotWinner) => {
      const exist = reducedWinnerData.find((t) => t.playerId === ele.playerId)
      if (!exist) {
        const temp = winners
          .filter((item: PotWinner) => item.playerId === ele.playerId)
          .reduce((acc, chipsWon) => {
            // TODO: FIX
            // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
            return { ...acc, chipsWon: acc.chipsWon + chipsWon.chipsWon }
          })
        reducedWinnerData.push(temp)
      }
    })
    return reducedWinnerData
  }
}

export { Snapshot, GameSnapshotter, TableSnapshotter, TableSnapshot }
